import cx from 'classnames'
import { MouseEvent, useEffect, useRef, useState } from 'react'
import { SearchMode } from '../../utils'
import styles from './search-mode-toggle.module.css'

type SearchModeToggleProps = {
  activeMode: SearchMode
  usedCarsUrl: string
  newCarsUrl: string
  className?: string
}

const NAVIGATION_DELAY_MS = 180

const shouldDelayNavigation = (event: MouseEvent<HTMLAnchorElement>) =>
  !event.defaultPrevented &&
  event.button === 0 &&
  !event.metaKey &&
  !event.ctrlKey &&
  !event.shiftKey &&
  !event.altKey

export const SearchModeToggle = ({
  activeMode,
  usedCarsUrl,
  newCarsUrl,
  className,
}: SearchModeToggleProps) => {
  const [pendingMode, setPendingMode] = useState<SearchMode | null>(null)
  const navigationTimerRef = useRef<number | null>(null)

  useEffect(
    () => () => {
      if (navigationTimerRef.current !== null) {
        window.clearTimeout(navigationTimerRef.current)
      }
    },
    [],
  )

  const handleToggleClick =
    (targetMode: SearchMode, targetUrl: string) =>
    (event: MouseEvent<HTMLAnchorElement>) => {
      if (!shouldDelayNavigation(event)) {
        return
      }

      event.preventDefault()
      setPendingMode(targetMode)

      navigationTimerRef.current = window.setTimeout(() => {
        window.location.assign(targetUrl)
      }, NAVIGATION_DELAY_MS)
    }

  const visibleActiveMode = pendingMode ?? activeMode

  return (
    <nav
      aria-label="Switch search mode"
      className={cx(styles.root, className, {
        [styles.modeUsed]: visibleActiveMode === 'used',
        [styles.modeNew]: visibleActiveMode === 'new',
        [styles.pending]: pendingMode !== null,
      })}
    >
      <a
        href={usedCarsUrl}
        className={cx(styles.option, {
          [styles.active]: visibleActiveMode === 'used',
        })}
        onClick={handleToggleClick('used', usedCarsUrl)}
      >
        Used cars
      </a>
      <a
        href={newCarsUrl}
        className={cx(styles.option, {
          [styles.active]: visibleActiveMode === 'new',
        })}
        onClick={handleToggleClick('new', newCarsUrl)}
      >
        New cars
      </a>
    </nav>
  )
}
